import React from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { FileText, X } from 'lucide-react';
import { useScrollLock } from '../hooks/useScrollLock';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const FileInfo = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const FileName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
  word-break: break-all;
`;

const FileDetails = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const ProgressSection = styled.div`
  margin-bottom: 1rem;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 12px;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 6px;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const IndeterminateProgressBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    animation: indeterminate 1.5s infinite;
  }
  
  @keyframes indeterminate {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const OverallProgressSection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f0f9ff;
  border-radius: 8px;
  border: 1px solid #bae6fd;
`;

const OverallProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e40af;
`;

const StatusMessage = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.75rem;
  text-align: center;
  min-height: 1.25rem;
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const TifConversionModal = ({ 
  isOpen, 
  fileName,
  currentPage = 0,
  totalPages = 0,
  progress = 0,
  status = 'Preparing...',
  currentFileIndex = 0,
  totalFiles = 0,
  overallProgress = 0
}) => {
  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  if (!isOpen) return null;

  // Determine if we should show indeterminate progress (when page count is unknown)
  const isIndeterminate = totalPages === 0;
  
  // Use overallProgress prop if provided, otherwise calculate from currentFileIndex
  const overallProgressPercent = overallProgress > 0 
    ? overallProgress 
    : (totalFiles > 0 ? Math.round(((currentFileIndex - 1) / totalFiles) * 100) : 0);

  const modalContent = (
    <ModalOverlay>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <FileText size={24} color="#3b82f6" />
            Converting TIF to PDF
          </Title>
        </Header>
        
        {/* Overall File Progress */}
        {totalFiles > 1 && (
          <OverallProgressSection>
            <OverallProgressLabel>
              <span>Overall Progress</span>
              <span>File {currentFileIndex} of {totalFiles} • {overallProgressPercent}%</span>
            </OverallProgressLabel>
            <ProgressBarContainer>
              <ProgressBar progress={overallProgressPercent} />
            </ProgressBarContainer>
          </OverallProgressSection>
        )}
        
        <FileInfo>
          <FileName>{fileName}</FileName>
          <FileDetails>
            {isIndeterminate ? (
              status
            ) : (
              <>
                Page {currentPage} of {totalPages}
                {currentPage > 0 && totalPages > 0 && ` • ${Math.round((currentPage / totalPages) * 100)}%`}
              </>
            )}
          </FileDetails>
        </FileInfo>
        
        {/* Current File Conversion Progress */}
        <ProgressSection>
          <ProgressLabel>
            <span>Current File Conversion</span>
            {!isIndeterminate && totalPages > 0 && (
              <span>{Math.round((currentPage / totalPages) * 100)}%</span>
            )}
          </ProgressLabel>
          <ProgressBarContainer>
            {isIndeterminate ? (
              <IndeterminateProgressBar />
            ) : (
              <ProgressBar progress={totalPages > 0 ? Math.round((currentPage / totalPages) * 100) : 0} />
            )}
          </ProgressBarContainer>
        </ProgressSection>
        
        <StatusMessage>
          {status && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <Spinner />
              <span>{status}</span>
            </div>
          )}
        </StatusMessage>
      </ModalContent>
    </ModalOverlay>
  );

  // Render modal outside of the component hierarchy using portal
  return createPortal(modalContent, document.body);
};

export default TifConversionModal;

